/**
 * =========================================================
 * Social Media Hub - Dark Mode Toggle
 * =========================================================
 *
 * Simple dark mode toggle panel with premium styling.
 */

import { useState, useEffect } from "react";

// @mui material components
import Switch from "@mui/material/Switch";
import Icon from "@mui/material/Icon";
import Box from "@mui/material/Box";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Custom styles for the Configurator
import ConfiguratorRoot from "examples/Configurator/ConfiguratorRoot";

// Material Dashboard 2 React context
import { useMaterialUIController, setOpenConfigurator, setDarkMode } from "context";

function Configurator() {
  const [controller, dispatch] = useMaterialUIController();
  const { openConfigurator, darkMode } = controller;

  const handleCloseConfigurator = () => setOpenConfigurator(dispatch, false);
  const handleDarkMode = () => setDarkMode(dispatch, !darkMode);

  return (
    <ConfiguratorRoot variant="permanent" ownerState={{ openConfigurator }}>
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        pt={3}
        pb={2}
        px={3}
      >
        <MDBox>
          <MDTypography
            variant="h5"
            fontWeight="600"
            sx={{
              color: "#0f172a",
              letterSpacing: "-0.01em",
            }}
          >
            Settings
          </MDTypography>
          <MDTypography
            variant="body2"
            sx={{
              color: "#64748b",
              fontSize: "0.85rem",
              mt: 0.5,
            }}
          >
            Customize your experience
          </MDTypography>
        </MDBox>

        <Box
          sx={{
            cursor: "pointer",
            width: 32,
            height: 32,
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
            },
          }}
          onClick={handleCloseConfigurator}
        >
          <Icon sx={{ color: "#64748b" }}>close</Icon>
        </Box>
      </MDBox>

      {/* Dark Mode Toggle */}
      <MDBox px={3} py={2}>
        <MDBox
          sx={{
            p: 2.5,
            borderRadius: "16px",
            backgroundColor: darkMode ? "#1e293b" : "#f8fafc",
            border: "1px solid",
            borderColor: darkMode ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.04)",
            transition: "all 0.3s ease",
          }}
        >
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox display="flex" alignItems="center" gap={1.5}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  background: darkMode
                    ? "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)"
                    : "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: darkMode
                    ? "0 4px 14px rgba(99, 102, 241, 0.3)"
                    : "0 4px 14px rgba(251, 191, 36, 0.3)",
                  transition: "all 0.3s ease",
                }}
              >
                <Icon sx={{ color: "white", fontSize: "1.3rem" }}>
                  {darkMode ? "dark_mode" : "light_mode"}
                </Icon>
              </Box>
              <MDBox>
                <MDTypography
                  variant="body2"
                  fontWeight="600"
                  sx={{
                    color: darkMode ? "white" : "#0f172a",
                    fontSize: "0.95rem",
                  }}
                >
                  {darkMode ? "Dark Mode" : "Light Mode"}
                </MDTypography>
                <MDTypography
                  variant="caption"
                  sx={{
                    color: darkMode ? "rgba(255, 255, 255, 0.6)" : "#64748b",
                    fontSize: "0.75rem",
                  }}
                >
                  {darkMode ? "Easy on your eyes" : "Bright and clean"}
                </MDTypography>
              </MDBox>
            </MDBox>

            <Switch
              checked={darkMode}
              onChange={handleDarkMode}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: "#6366f1",
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: "#6366f1",
                },
              }}
            />
          </MDBox>
        </MDBox>
      </MDBox>

      {/* Info Section */}
      <MDBox px={3} py={2}>
        <MDBox
          sx={{
            p: 2,
            borderRadius: "12px",
            backgroundColor: darkMode ? "rgba(99, 102, 241, 0.1)" : "rgba(99, 102, 241, 0.05)",
            border: "1px solid",
            borderColor: darkMode ? "rgba(99, 102, 241, 0.2)" : "rgba(99, 102, 241, 0.1)",
          }}
        >
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon sx={{ color: "#6366f1", fontSize: "1.1rem" }}>info</Icon>
            <MDTypography
              variant="caption"
              sx={{
                color: darkMode ? "rgba(255, 255, 255, 0.7)" : "#64748b",
                fontSize: "0.8rem",
                lineHeight: 1.5,
              }}
            >
              Dark mode reduces eye strain in low-light environments and saves battery on OLED
              screens.
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
    </ConfiguratorRoot>
  );
}

export default Configurator;
